/**
 * Color contrast helpers (WCAG) — readable text on a given background,
 * and HTML email preview adaptation for dark/light themes.
 */

export const getRGB = (c: string | number) => Number.parseInt(String(c), 16) || c

export const getsRGB = (c: string) => {
  const channel = Number(getRGB(c)) / 255
  return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

/** Normalize `#RGB`, `#RRGGBB` or `#RRGGBBAA` to `#rrggbb`. */
export const normalizeHexColor = (hexColor: string): string | null => {
  if (!hexColor) return null
  let value = String(hexColor).trim().replace(/^#/, '')
  if (value.length === 3 || value.length === 4)
    value = value.slice(0, 3).split('').map(c => c + c).join('')
  else if (value.length >= 6)
    value = value.slice(0, 6)
  return /^[0-9a-fA-F]{6}$/.test(value) ? `#${value.toLowerCase()}` : null
}

export const getLuminance = (hexColor: string) => {
  const hex = normalizeHexColor(hexColor)
  if (!hex) return 0
  return 0.2126 * getsRGB(hex.slice(1, 3))
    + 0.7152 * getsRGB(hex.slice(3, 5))
    + 0.0722 * getsRGB(hex.slice(5, 7))
}

export const getContrast = (a: string, b: string) => {
  const L1 = getLuminance(a)
  const L2 = getLuminance(b)
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05)
}

/** Best readable text color (black or white) for a background. */
export const getTextColor = (bgColor: string) => {
  return getContrast(bgColor, '#ffffff') > getContrast(bgColor, '#000000')
    ? '#ffffff'
    : '#000000'
}

const NAMED_CSS_COLORS: Record<string, string> = {
  black: '#000000',
  white: '#ffffff',
  gray: '#808080',
  grey: '#808080',
  silver: '#c0c0c0',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  maroon: '#800000',
  yellow: '#ffff00',
  olive: '#808000',
  lime: '#00ff00',
  aqua: '#00ffff',
  teal: '#008080',
  navy: '#000080',
  fuchsia: '#ff00ff',
  purple: '#800080',
}

/** Parse a CSS color token to `#rrggbb` when possible. */
export const parseCssColorToHex = (raw: string): string | null => {
  if (!raw) return null
  const value = raw.trim().toLowerCase().replace(/\s+/g, '')
  if (!value || value === 'transparent' || value === 'inherit' || value === 'currentcolor') return null
  if (value.includes('gradient') || value.includes('url(')) return null
  if (NAMED_CSS_COLORS[value]) return NAMED_CSS_COLORS[value]
  if (value.startsWith('#')) return normalizeHexColor(value)

  const rgb = value.match(/^rgba?\((\d+),(\d+),(\d+)(?:,([\d.]+))?\)$/)
  if (!rgb) return null
  const toHex = (n: string) => Number(n).toString(16).padStart(2, '0')
  return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`
}

/** Extract a full CSS color token (supports `rgb(0, 0, 0)`, hex, named). */
export const extractCssColorToken = (colorValue: string): string => {
  const trimmed = colorValue.trim().replace(/\s*!important\s*$/i, '').trim()
  const functional = trimmed.match(/^(rgba?|hsla?)\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*[\d.]+\s*)?\)/i)
  return functional?.[0] || trimmed.split(/\s+/)[0] || trimmed
}

/** Black / white / gray only (keeps intentional red, green, blue, …). */
export const isNeutralCssColor = (color: string): boolean => {
  const hex = parseCssColorToHex(color)
  if (!hex) return false
  const r = Number.parseInt(hex.slice(1, 3), 16)
  const g = Number.parseInt(hex.slice(3, 5), 16)
  const b = Number.parseInt(hex.slice(5, 7), 16)
  return Math.max(r, g, b) - Math.min(r, g, b) <= 28
}

/**
 * If contrast is below WCAG AA (4.5), return black or white via getTextColor.
 * Otherwise keep the original text color.
 */
export const ensureReadableTextColor = (
  textColor: string,
  bgColor: string,
  minContrast = 4.5,
): string => {
  const bgHex = parseCssColorToHex(bgColor) || '#ffffff'
  const textHex = parseCssColorToHex(textColor)
  if (!textHex) return getTextColor(bgHex)
  if (getContrast(textHex, bgHex) >= minContrast) return textColor.trim()
  return getTextColor(bgHex)
}

const solidBgFromStyle = (style: string, fallbackBg: string) => {
  const match = style.match(/(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i)
  return (match && parseCssColorToHex(match[1])) || fallbackBg
}

const adaptNeutralColor = (token: string, bg: string, minContrast: number) => {
  if (!isNeutralCssColor(token)) return null
  return ensureReadableTextColor(token, bg, minContrast)
}

/**
 * Rewrite HTML inline text colors that fail contrast.
 * Only adapts neutral colors; leaves chromatic colors and backgrounds unchanged.
 */
export const adaptHtmlColorsForContrast = (
  html: string,
  fallbackBg: string,
  minContrast = 4.5,
): string => {
  if (!html) return ''

  return html
    .replace(/style=(["'])([\s\S]*?)\1/gi, (_full, quote: string, style: string) => {
      const bg = solidBgFromStyle(style, fallbackBg)
      const next = style.replace(/(?<![\w-])color\s*:\s*([^;]+)/gi, (match, value: string) => {
        const adapted = adaptNeutralColor(extractCssColorToken(value), bg, minContrast)
        return adapted ? `color:${adapted}` : match
      })
      return `style=${quote}${next}${quote}`
    })
    .replace(/\scolor\s*=\s*(["']?)([^"'\s>]+)\1/gi, (full, _q: string, value: string) => {
      const adapted = adaptNeutralColor(value, fallbackBg, minContrast)
      return adapted ? ` color="${adapted}"` : full
    })
}
